import { createSelector } from '@reduxjs/toolkit';
import { LoadingStatus } from 'shared/types';
import type { RootState } from 'store';

import { DEPRECATED_OFFER_ERROR_CODES } from '../constants';
import { getOfferType, getSelectAllState } from '../helpers';
import { OfferErrorType, OfferType } from '../types';

const offerStateSelector = (state: RootState) => state.candidateOffer;

export const statusSelector = createSelector(
    offerStateSelector,
    (state) => state.status
);

export const offerSelector = createSelector(
    offerStateSelector,
    (state) => state.offer
);

export const offerTypeSelector = createSelector(
    offerSelector,
    (offer): OfferType | null => {
        if (!offer) {
            return null;
        }

        return getOfferType(offer);
    }
);

export const shiftsSelector = createSelector(
    offerSelector,
    (offer) => offer?.shifts ?? []
);

export const selectedShiftIdsSelector = createSelector(
    offerStateSelector,
    (state) => state.selectedShiftIds
);

export const shiftsSelectAllStateSelector = createSelector(
    shiftsSelector,
    selectedShiftIdsSelector,
    (shifts, selection) => getSelectAllState(shifts, selection)
);

export const noteSelector = createSelector(
    offerStateSelector,
    (state) => state.note
);

export const selectedPeriodIdsSelector = createSelector(
    offerStateSelector,
    (state) => state.selectedPeriodIds
);

export const selectedPeriodsCountSelector = createSelector(
    selectedPeriodIdsSelector,
    (selectedWeeks) => selectedWeeks.length
);

export const submitErrorSelector = createSelector(
    offerStateSelector,
    (state) => state.submitState.error
);

export const submitWarningSelector = createSelector(
    offerStateSelector,
    (state) => state.submitState.warning
);

export const submitStatusSelector = createSelector(
    offerStateSelector,
    (state) => state.submitState.status
);

const errorSelector = createSelector(
    offerStateSelector,
    (state) => state.error
);

export const errorTypeSelector = createSelector(
    errorSelector,
    submitErrorSelector,
    (loadError, submitError): OfferErrorType | null => {
        if (!loadError && !submitError) {
            return null;
        }

        const isDeprecatedOffer = [ loadError, submitError ].some((error) => {
            return DEPRECATED_OFFER_ERROR_CODES.some((code) => code === error);
        });

        if (isDeprecatedOffer) {
            return 'deprecated';
        }

        return 'general';
    }
);

export const pageStatusSelector = createSelector(
    statusSelector,
    errorTypeSelector,
    (status, errorType): LoadingStatus => {
        if (errorType === 'deprecated') {
            return 'error';
        }

        return status;
    }
);

export const selectedItemsCountSelector = createSelector(
    offerTypeSelector,
    selectedShiftIdsSelector,
    selectedPeriodIdsSelector,
    (offerType, selectedShiftIds, selectedPeriodIds) => (offerType === 'Shifts' ? selectedShiftIds.length : selectedPeriodIds.length)
);
